import { LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { useMemberPricingContext } from "@/hooks/useMemberPricingContext";
import { shopDiscountsQuery, useShopDiscounts } from "@/hooks/useShopDiscounts";
import { isUuid, toCatalogCard } from "@/utils/algoliaCatalog";
import {
  getPlatform,
  getPromotions,
  getRetailproductsAreRetailProductsVisible,
  getShop,
  getShopCustomCategoriesListings,
  getSkindropIsVisibleOnMarketplace,
  getUser,
} from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import { fetchCatalogLocationId } from "hooks/useCatalogLocationId";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { NextFilterAndSortListings } from "pages/root/shop/NextFilterAndSortListings";
import { getFreeGiftPackageIds } from "services/ListingHelperService";
import { waitObj } from "toolbox/Promise";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

const load = async (concernId: string) => {
  const shopData = queryClient.fetchQuery({ ...getShop.keyed() });
  const platform = queryClient.fetchQuery({
    ...getPlatform.keyed(),
    staleTime: STALE_TIME.platformConfig,
  });
  const promotions = queryClient.fetchQuery({
    ...getPromotions.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  // Badges fill in when this lands; the page does not wait for it.
  void queryClient.prefetchQuery(shopDiscountsQuery());
  const customShopCategories = queryClient.fetchQuery({
    ...getShopCustomCategoriesListings.keyed(),
    staleTime: 1000 * 10,
  });
  const user = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  const areRetailProductsVisible = queryClient.fetchQuery({
    ...getRetailproductsAreRetailProductsVisible.keyed(),
    staleTime: 1000 * 10,
  });
  // Skindrop visibility is non-critical; if the endpoint fails, default to false
  const isSkinDropVisible = queryClient
    .fetchQuery({
      ...getSkindropIsVisibleOnMarketplace.keyed(),
      staleTime: 1000 * 10,
      retry: false,
    })
    .catch(() => false);
  const platformLocationId = user.then((userData) => fetchCatalogLocationId(queryClient, userData));

  return waitObj({
    concernId,
    shopData,
    platform,
    promotions,
    customShopCategories,
    user,
    platformLocationId,
    areRetailProductsVisible,
    isSkinDropVisible,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const shopByConcernLoader: LoaderFunction = ({ params: { id } }) => {
  // The id is interpolated into the index filter; anything but a UUID is not a concern.
  if (!id || !isUuid(id)) return redirect("/shop");
  return load(id);
};

const ShopByConcernPage = () => {
  const { t } = useTranslation();
  const {
    concernId,
    shopData,
    platform,
    promotions,
    customShopCategories,
    user,
    platformLocationId,
    areRetailProductsVisible,
    isSkinDropVisible,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const shopDiscounts = useShopDiscounts();
  const memberPricing = useMemberPricingContext();

  const cardContext = {
    shopDiscounts,
    customShopCategories,
    freeGiftPackageIds: getFreeGiftPackageIds(promotions),
    memberPricing,
    retailMemberPrices: undefined,
  };

  const concern = shopData.concerns?.find((concern) => concern.id == concernId);

  return (
    <>
      <TopNav startIconSVG={ChevronLeft} onStartIconClick={() => navigate("/shop/concerns")}>
        <div className="flex max-h-[32px] items-center">
          <h1 className="text-xl">{t("shopByConcernPage.title")}</h1>
        </div>
      </TopNav>
      <div className="flex h-full flex-col bg-two">
        {concern && concern.name && (
          <ShopListHeader
            title={concern.name}
            subtitle={concern.description ?? ""}
            bgGradient
            backgroundColor="rgb(var(--repeatmd-brand-color))"
          />
        )}
        <NextFilterAndSortListings
          surface="concern"
          types={["Treatment", "Package"]}
          areas={[]}
          concerns={[]}
          concernId={concernId}
          platformId={platform.id}
          platformLocationId={platformLocationId}
          visibility={{ areRetailProductsVisible, isSkinDropVisible }}
          shopDiscounts={shopDiscounts}
        >
          {(hit) => {
            const card = toCatalogCard(hit, cardContext);

            return (
              <ProductCardLarge
                itemId={card.id}
                title={card.title}
                description={card.description}
                imageUrl={card.imageUrl}
                type={card.type}
                tag=""
                concerns={card.concerns}
                price={card.price}
                memberPrice={card.memberPrice}
                onClick={() => navigate(card.pdpPath)}
                hasLinearPricing={card.hasLinearPricing}
                unitType={card.unitType}
                discount={card.discountText}
                discountData={card.discount}
                customColor={card.customColor}
                guestMode={user.isGuest}
              />
            );
          }}
        </NextFilterAndSortListings>
        <RepeatMDFooter />
      </div>
    </>
  );
};

export default ShopByConcernPage;
